import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { XIcon } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  dialogFooterBaseClassName,
  dialogFooterDividerClassName,
  dialogFooterTertiaryButtonClassName,
  dialogHeaderVariantClasses,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { isEventFromReactSelectMenu } from '#/modules/commonComponents/dialog/isEventFromReactSelectMenu';
import {
  Dialog as VendorDialog,
  DialogClose as VendorDialogClose,
  DialogPortal as VendorDialogPortal,
  DialogTrigger as VendorDialogTrigger,
} from '#/modules/commonComponents/shadcn/ui/dialog';

const Dialog = VendorDialog;
const DialogTrigger = VendorDialogTrigger;
const DialogPortal = VendorDialogPortal;
const DialogClose = VendorDialogClose;

function DialogOverlay({
  className,
  ...props
}) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // eslint-disable-next-line max-len
        'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  headerVariant = 'default',
  closeOnOutsideClick = true,
  closeOnEscape = true,
  onPointerDownOutside,
  onInteractOutside,
  onFocusOutside,
  onEscapeKeyDown,
  ...props
}) {
  function handlePointerDownOutside(event) {
    if (isEventFromReactSelectMenu(event)) {
      event.preventDefault();
      onPointerDownOutside?.(event);
      return;
    }
    if (!closeOnOutsideClick) {
      event.preventDefault();
    }
    onPointerDownOutside?.(event);
  }

  function handleInteractOutside(event) {
    if (isEventFromReactSelectMenu(event)) {
      event.preventDefault();
      onInteractOutside?.(event);
      return;
    }
    if (!closeOnOutsideClick) {
      event.preventDefault();
    }
    onInteractOutside?.(event);
  }

  function handleFocusOutside(event) {
    if (isEventFromReactSelectMenu(event)) {
      event.preventDefault();
      onFocusOutside?.(event);
      return;
    }
    onFocusOutside?.(event);
  }

  function handleEscapeKeyDown(event) {
    if (!closeOnEscape) {
      event.preventDefault();
    }
    onEscapeKeyDown?.(event);
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // eslint-disable-next-line max-len
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onFocusOutside={handleFocusOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}
      >
        {children}
        {showCloseButton ? (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button
              variant="ghost"
              className={cn(
                'absolute top-2 right-2',
                headerVariant === 'primary'
                  && 'text-white hover:bg-white/10 hover:text-white',
              )}
              size="icon-sm"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  variant = 'default',
  ...props
}) {
  return (
    <div
      data-slot="dialog-header"
      data-variant={variant}
      className={cn(dialogHeaderVariantClasses[variant], className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        dialogFooterBaseClassName,
        dialogFooterDividerClassName,
        dialogFooterTertiaryButtonClassName,
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'text-lg leading-tight font-semibold',
        'group-data-[variant=muted]/dialog-header:text-slate-900',
        'group-data-[variant=primary]/dialog-header:text-white',
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        'group-data-[variant=muted]/dialog-header:text-slate-600',
        'group-data-[variant=muted]/dialog-header:*:[a]:hover:text-slate-900',
        'group-data-[variant=primary]/dialog-header:text-primary-100',
        'group-data-[variant=primary]/dialog-header:*:[a]:hover:text-white',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
